import { apiClient } from "../api/apiClient.js";

// Prompt 141: War Room's real, scope-isolated roster source - every person
// in the caller's scope (Quick Registered applicants, e-KYC-in-progress
// applicants, and every promoted worker), never gated on whether
// dbo.WmxWorkerLatestStatus happens to have a heartbeat row for them. This
// replaces the previous Prompt 135 wiring, which read
// GET .../tracking/workers/latest as the PRIMARY roster - that endpoint only
// ever returns a row once a heartbeat has arrived, so a freshly seeded/
// registered person silently disappeared from the board. See
// BackofficeWarRoomController.cs/WarRoomService.cs/WarRoomRepository.cs for
// the real server-side scope resolution (SuperAdmin sees everything;
// SuperAgent/PLC/Agent see only their own branch) and the Combined/Scored
// SQL CTEs that make "exists on the roster" and "has ever sent a heartbeat"
// two independent questions.
//
// GET .../war-room/workers is real, server-side-paginated (pageSize
// hard-capped at 5000 server-side - WarRoomService.MaxPageSize) - this file
// never falls back to a fetch-everything/no-limit request even though the
// underlying tables can hold 200,000+ rows.
const WAR_ROOM_PATH = "/backoffice/war-room/workers";

// Exactly the query param names BackofficeWarRoomController.List declares -
// anything else passed to listWarRoomWorkers is silently dropped rather than
// forwarded, so War Room can never accidentally send an unbounded/unexpected
// param (e.g. a fake "pageSize=999999").
const WAR_ROOM_LIST_PARAM_KEYS = [
  "search",
  "statusCategory",
  "applicationStatus",
  "workerStatus",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "sortBy",
  "sortDirection",
  "page",
  "pageSize",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listWarRoomWorkers(params = {}) {
  return apiClient.get(`${WAR_ROOM_PATH}${buildQuery(WAR_ROOM_LIST_PARAM_KEYS, params)}`);
}

// Detail requires a real, non-empty personId + personType - fetched only
// once a seat is actually clicked, never prefetched for every seat on the
// board. personType must be the exact value the list row itself carried
// ("WorkerApplication" or "WorkerProfile") - there is no way to infer it
// from personId alone (ids are opaque VARCHAR(40) values in production).
export function getWarRoomPersonDetail(personId, personType) {
  if (!personId) {
    throw new Error("getWarRoomPersonDetail requires a personId");
  }
  if (!personType) {
    throw new Error("getWarRoomPersonDetail requires a personType");
  }
  return apiClient.get(`${WAR_ROOM_PATH}/${encodeURIComponent(personId)}?personType=${encodeURIComponent(personType)}`);
}
